import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { Album, CheckCircle, ErrorOutline, QueueMusic } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const CUE_ROWS = [
  { key: 'cue_in', label: 'Cue In', marker: 1 },
  { key: 'mix_done', label: 'Mix Done', marker: 2 },
  { key: 'outro_start', label: 'Outro Start', marker: 3 },
  { key: 'mix_end', label: 'Mix End', marker: 4 },
];

const MARKER_TYPES = { 0: 'Cue', 1: 'Fade-in', 2: 'Fade-out', 3: 'Load', 4: 'Loop' };

// Must match the server's mix-cue file-end tolerance (beatmix-support).
const CUE_PAST_END_TOLERANCE_SEC = 1;

function hasValue(value) {
  return value != null && Number.isFinite(Number(value));
}

function formatCueTime(seconds) {
  if (!hasValue(seconds)) return '—';
  const hundredths = Math.round(Number(seconds) * 100);
  const mins = Math.floor(hundredths / 6000);
  const secs = ((hundredths % 6000) / 100).toFixed(2).padStart(5, '0');
  return `${mins}:${secs}`;
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function matchLabel(confidence) {
  if (!hasValue(confidence)) return '—';
  if (Number(confidence) >= 1) return 'Exact / manual link';
  return `Automatic match (${Math.round(Number(confidence) * 100)}%)`;
}

function markerSlot(num) {
  if (num === -1) return 'Memory cue';
  if (Number.isInteger(num) && num >= 0 && num < 26) return `Hot cue ${String.fromCharCode(65 + num)}`;
  return '—';
}

function downbeatLabel(meta) {
  if (!meta) return 'Pending analysis';
  if (meta.source === 'manual') return `Manual rotation +${meta.rotationBeats}`;
  if (meta.status === 'ok' && Number(meta.confidence) >= 0.72) {
    return `Automatic +${meta.rotationBeats} (${Math.round(Number(meta.confidence) * 100)}% confidence)`;
  }
  if (meta.status === 'ambiguous') return 'Needs review';
  return 'Marker lock';
}

// Mix End is optional and stored as 0 when the fourth marker is absent.
function cueValue(key, value) {
  if (!hasValue(value)) return null;
  const number = Number(value);
  if (key === 'mix_end' && number <= 0) return null;
  return number;
}

function cueRoleForMarker(marker, track) {
  const row = CUE_ROWS.find(({ key }) => {
    const value = cueValue(key, track?.[key]);
    return value != null && Math.abs(value - marker.start) < 0.001;
  });
  return row ? row.label : null;
}

const chipSx = (color) => ({
  bgcolor: `${color}1f`,
  color,
  border: `1px solid ${color}59`,
  fontSize: '0.75rem',
  '& .MuiChip-icon': { color },
});

function DetailRow({ label, children, mono = false }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 1.5, py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.primary',
          wordBreak: 'break-word',
          ...(mono ? { fontFamily: 'monospace', fontSize: '0.8rem' } : {}),
        }}
      >
        {children ?? '—'}
      </Typography>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography
      variant="overline"
      sx={{ display: 'block', color: '#a78bfa', fontWeight: 700, letterSpacing: 1, mt: 2.5, mb: 0.5 }}
    >
      {children}
    </Typography>
  );
}

export default function RekordboxInfoDialog({ open, asset, stationId, onClose, onOpenBeatmix }) {
  // Results are keyed by asset and station so a previous lookup never shows under a new title.
  const [result, setResult] = useState(null);
  const requestRef = useRef(0);
  const assetId = asset?.id;
  const resultKey = `${assetId}:${stationId || ''}`;

  useEffect(() => {
    if (!open || !assetId) return;
    const request = ++requestRef.current;
    const key = `${assetId}:${stationId || ''}`;
    axios.get(`${API_URL}/api/assets/${assetId}/rekordbox`, {
      params: stationId ? { station_id: stationId } : undefined,
      withCredentials: true,
    })
      .then((res) => {
        if (request === requestRef.current) setResult({ key, data: res.data, error: null });
      })
      .catch((err) => {
        if (request === requestRef.current) {
          setResult({
            key,
            data: null,
            error: err.response?.data?.message || 'Could not load Rekordbox info',
          });
        }
      });
  }, [open, assetId, stationId]);

  const current = result?.key === resultKey ? result : null;
  const info = current?.data || null;
  const error = current?.error || null;
  const loading = open && !!assetId && !current;
  const mixAsset = info?.asset;
  const heading = asset || mixAsset;
  const track = info?.tracks?.[0] || null;
  const otherTracks = info?.tracks?.slice(1) || [];
  const libraryCues = mixAsset?.mix_cue_points || {};
  const fileDuration = Number(mixAsset?.duration) || 0;
  const cellSx = { color: 'text.primary', borderBottom: '1px solid var(--rs-border)', py: 0.75 };
  const headSx = { ...cellSx, color: 'text.secondary', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase' };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: 'var(--rs-paper)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Album sx={{ color: '#a78bfa' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>Rekordbox info</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {[heading?.title, heading?.artist].filter(Boolean).join(' — ') || '—'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: 'var(--rs-paper)' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#a78bfa' }} />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

        {mixAsset && (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {mixAsset.is_mix_version && (
                <Chip size="small" label="Mix version" sx={chipSx('#fbbf24')} />
              )}
              <Chip
                size="small"
                icon={mixAsset.has_mix_cues ? <CheckCircle /> : <ErrorOutline />}
                label={mixAsset.has_mix_cues ? 'Cue points applied' : 'No cue points yet'}
                sx={chipSx(mixAsset.has_mix_cues ? '#4ade80' : '#94a3b8')}
              />
              <Chip
                size="small"
                icon={track ? <CheckCircle /> : <ErrorOutline />}
                label={track ? 'Linked to Rekordbox' : 'Not linked to Rekordbox'}
                sx={chipSx(track ? '#a78bfa' : '#fbbf24')}
              />
            </Box>

            {info.mix_upload && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                Uploaded through Beat Mixing from <strong>{info.mix_upload.original_filename}</strong>
                {' '}on {formatDate(info.mix_upload.finished_at || info.mix_upload.uploaded_at)}.
              </Typography>
            )}

            {!track && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This asset is not linked to a Rekordbox track yet. Upload a Rekordbox XML, or link it
                from Beat Mixing → Rekordbox Tracks.
              </Alert>
            )}

            {track && (
              <>
                <SectionTitle>Rekordbox track</SectionTitle>
                <DetailRow label="Track">{track.track_name}</DetailRow>
                <DetailRow label="Artist">{track.track_artist || '—'}</DetailRow>
                <DetailRow label="BPM">{hasValue(track.bpm) ? track.bpm.toFixed(2) : '—'}</DetailRow>
                <DetailRow label="Key">{track.tonality || '—'}</DetailRow>
                <DetailRow label="Rekordbox TrackID">{track.track_id_xml || '—'}</DetailRow>
                <DetailRow label="XML file">
                  {track.xml_filename
                    ? `${track.xml_filename} (uploaded ${formatDate(track.xml_uploaded_at)})`
                    : '—'}
                </DetailRow>
                <DetailRow label="Link">{matchLabel(track.match_confidence)}</DetailRow>
                <DetailRow label="Beatgrid">
                  {track.beatgrid_entries > 0
                    ? `${track.beatgrid_entries} tempo marker${track.beatgrid_entries === 1 ? '' : 's'}`
                    : 'None in XML'}
                </DetailRow>
                <DetailRow label="Original file" mono>{track.original_location || '—'}</DetailRow>
              </>
            )}

            <SectionTitle>Cue points</SectionTitle>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>Marker</TableCell>
                  <TableCell sx={headSx}>Rekordbox XML</TableCell>
                  <TableCell sx={headSx}>Library (used on air)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {CUE_ROWS.map(({ key, label, marker }) => {
                  const fromXml = cueValue(key, track?.[key]);
                  const fromLibrary = mixAsset.has_mix_cues ? cueValue(key, libraryCues[key]) : null;
                  const differs = fromXml != null && fromLibrary != null
                    && Math.abs(fromXml - fromLibrary) >= 0.01;
                  const pastEnd = (value) => value != null && fileDuration > 0
                    && value > fileDuration + CUE_PAST_END_TOLERANCE_SEC;
                  const libraryNote = pastEnd(fromLibrary)
                    ? 'Past the end of this file'
                    : (differs ? 'Differs from the Rekordbox XML' : undefined);
                  return (
                    <TableRow key={key}>
                      <TableCell sx={cellSx}>{marker}. {label}</TableCell>
                      <TableCell
                        sx={{ ...cellSx, fontVariantNumeric: 'tabular-nums', color: pastEnd(fromXml) ? '#f87171' : cellSx.color }}
                        title={pastEnd(fromXml) ? 'Past the end of this file' : undefined}
                      >
                        {track ? formatCueTime(fromXml) : '—'}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellSx,
                          fontVariantNumeric: 'tabular-nums',
                          color: pastEnd(fromLibrary) ? '#f87171' : (differs ? '#fbbf24' : cellSx.color),
                        }}
                        title={libraryNote}
                      >
                        {formatCueTime(fromLibrary)}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell sx={cellSx}>File length</TableCell>
                  <TableCell sx={cellSx}>—</TableCell>
                  <TableCell sx={{ ...cellSx, fontVariantNumeric: 'tabular-nums' }}>
                    {fileDuration > 0 ? formatCueTime(fileDuration) : '—'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={cellSx}>BPM / Key</TableCell>
                  <TableCell sx={cellSx}>
                    {track
                      ? [hasValue(track.bpm) ? track.bpm.toFixed(1) : null, track.tonality].filter(Boolean).join(' · ') || '—'
                      : '—'}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {[hasValue(mixAsset.mix_bpm) ? mixAsset.mix_bpm.toFixed(1) : null, mixAsset.mix_key]
                      .filter(Boolean).join(' · ') || '—'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={cellSx}>Beat-in-bar</TableCell>
                  <TableCell sx={cellSx}>—</TableCell>
                  <TableCell sx={cellSx}>
                    {mixAsset.has_mix_cues ? downbeatLabel(mixAsset.mix_beat_meta) : '—'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {track && track.marker_warnings?.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {track.markers_complete
                  ? 'Rekordbox marker notes: '
                  : 'These Rekordbox markers are incomplete, so they were not applied: '}
                {track.marker_warnings.join('; ')}.
              </Alert>
            )}

            {track && track.markers?.length > 0 && (
              <>
                <SectionTitle>Rekordbox markers</SectionTitle>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>Slot</TableCell>
                      <TableCell sx={headSx}>Name</TableCell>
                      <TableCell sx={headSx}>Type</TableCell>
                      <TableCell sx={headSx}>Time</TableCell>
                      <TableCell sx={headSx}>Used as</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...track.markers]
                      .sort((a, b) => a.start - b.start)
                      .map((marker, index) => (
                        <TableRow key={`${marker.num}-${marker.start}-${index}`}>
                          <TableCell sx={cellSx}>{markerSlot(marker.num)}</TableCell>
                          <TableCell sx={cellSx}>{marker.name || '—'}</TableCell>
                          <TableCell sx={cellSx}>
                            {marker.type == null ? '—' : (MARKER_TYPES[marker.type] || `Type ${marker.type}`)}
                          </TableCell>
                          <TableCell sx={{ ...cellSx, fontVariantNumeric: 'tabular-nums' }}>
                            {formatCueTime(marker.start)}
                          </TableCell>
                          <TableCell sx={{ ...cellSx, color: '#a78bfa' }}>
                            {cueRoleForMarker(marker, track) || ''}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </>
            )}

            {otherTracks.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Also linked to {otherTracks.length} older Rekordbox entr{otherTracks.length === 1 ? 'y' : 'ies'}:{' '}
                {otherTracks
                  .map((other) => `${other.track_name}${other.xml_filename ? ` (${other.xml_filename})` : ''}`)
                  .join(', ')}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ bgcolor: 'var(--rs-paper)' }}>
        {onOpenBeatmix && (
          <Button startIcon={<QueueMusic />} onClick={onOpenBeatmix} sx={{ color: '#a78bfa' }}>
            Open Beat Mixing
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
